<template>
  <div class="custom-sidebar" :class="{ 'is-mobile': isMobile }">
    <div class="sidebar-header" v-if="!isApp">
      <div class="logo-container">
        <img :src="logoUrl" alt="logo" class="logo" />
      </div>

      <v-divider class="my-4" />
    </div>

    <div class="sidebar-content">
      <team-switcher class="mx-4" v-if="teamsCount > 1" />

      <div class="user-profile" v-else>
        <div class="profile-info">
          <div class="profile-avatar">
            <img v-if="profileImage" :src="profileImage" :alt="username || userEmail" class="avatar" />

            <div v-else class="avatar-placeholder">
              {{ initials }}
            </div>
          </div>

          <div class="profile-details">
            <div class="username">{{ username || userEmail || 'Perfil' }}</div>
            <div class="user-role" v-if="userRole">{{ userRole }}</div>
          </div>
        </div>
      </div>

      <view-toggle
        v-model="dashboard"
        v-if="canToggleView && !isAdminOrSuperAdmin && !isSupplier"
        class="px-4 mt-2"
      />

      <v-divider class="my-4" />

      <template v-if="!isAdminOrSuperAdmin">
        <supplier-menu v-if="isSupplier" :disabled="mustAcceptTerms" />

        <template v-else>
          <doctor-menu v-if="isDoctorDashboard" />
  
          <owner-menu
            :disabled="mustAcceptTerms"
            v-if="!isDoctorDashboard && !canShowSaasItems && isOldUser"
          />
  
          <saas-menu
            :disabled="mustAcceptTerms"
            v-if="!$isApp && !isDoctorDashboard && canShowSaasItems"
          />
  
          <template v-if="!isTeamUser">
            <v-divider class="my-4" />
      
            <common-menu
              :disabled="mustAcceptTerms"
              :view="dashboard"
            />
          </template>
        </template>
      </template>

      <template v-if="isSuperAdmin">
        <super-admin-menu  />
        <v-divider class="my-4" />
      </template>

      <admin-menu v-if="isAdminOrSuperAdmin" />

      <v-divider class="my-4" />

      <menu-item
        :item="logoutItem"
      />
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';

import { IS_APP } from '@/constants';
import AdminMenu from '@/components/menu/AdminMenu';
import DoctorMenu from '@/components/menu/DoctorMenu';
import CommonMenu from '@/components/menu/CommonMenu';
import MenuItem from '@/components/menu/MenuItem.vue';
import OwnerMenu from '@/components/menu/OwnerMenu';
import SaasMenu from '@/components/menu/SaasMenu';
import SuperAdminMenu from '@/components/menu/SuperAdminMenu';
import TeamSwitcher from '@/components/teams/TeamSwitcher';
import ViewToggle from '@/components/menu/ViewToggle';
import SupplierMenu from '@/components/menu/SupplierMenu.vue';

export default {
  name: 'Sidebar',

  components: {
    AdminMenu,
    DoctorMenu,
    CommonMenu,
    MenuItem,
    OwnerMenu,
    SaasMenu,
    SuperAdminMenu,
    TeamSwitcher,
    ViewToggle,
    SupplierMenu,
  },

  data() {
    return {
      isApp: IS_APP,
      dashboard: null,
      logoutItem: {
        title: 'Sair',
        icon: 'mdi-exit-to-app',
        onClick: () => this.$store.dispatch('user/signout'),
        iconColor: 'default',
      },
    };
  },

  mounted() {
    this.dashboard = (this.isApp || this.preferences?.useDoctorDashboard)
      ? 'doctor'
      : 'owner';
  },

  computed: {
    ...mapGetters('app', {
      appConfig: 'GET_CONFIG',
    }),
    ...mapGetters('subscription', {
      saasSubscription: 'GET_SAAS_SUBSCRIPTION',
    }),
    ...mapGetters('team', {
      teamsCount: 'GET_TEAMS_COUNT',
      teamRole: 'GET_ROLE',
    }),
    ...mapGetters('termsOfUse', {
      termsAccepted: 'ACCEPTED'
    }),
    ...mapGetters('user', {
      readOnly: 'GET_READONLY_INFO',
      preferences: 'GET_PREFERENCES',
      userInfo: 'GET_USER_INFO',
    }),

    isSupplier() {
      return this.userInfo?.isSupplier || this.$route.path.includes('supplier');
    },

    canShowSaasItems() {
      return !this.isOldUser
          || this.hasSaasSubscription
          && this.readOnly?.ownerSaasId
          || this.readOnly?.saasRole
          && this.readOnly?.saasRole !== 'user';
    },

    canToggleView() {
      return !this.isTeamMember && !this.isApp && !this.isSaas;
    },

    hasSaasSubscription() {
      return !!this.saasSubscription;
    },

    initials() {
      if (!this.userInfo?.name) {
        return null;
      }
      return this.userInfo.name.split(' ').slice(0, 2).map(s => s.charAt(0)).join('');
    },

    isDoctorDashboard() {
      if (this.isApp || this.isTeamMember) {
        return true;
      }

      if (this.isSaas) {
        return !this.isSaasTeam;
      }

      return this.canToggleView && this.dashboard === 'doctor';
    },

    isSuperAdmin() {
      return this.readOnly?.isSuperAdmin;
    },

    isAdminOrSuperAdmin() {
      return this.readOnly?.isAdmin || this.isSuperAdmin;
    },

    isMobile() {
      return this.$vuetify.breakpoint.mobile;
    },

    isOldUser() {
      return this.readOnly?.isOldUser;
    },

    isSaas() {
      return !!this.readOnly?.saasId;
    },

    isSaasTeam() {
      return this.isSaas && this.readOnly?.saasRole !== 'user';
    },

    isTeamMember() {
      return this.teamRole && this.teamRole !== 'owner';
    },

    isTeamUser() {
      return this.teamRole === 'user';
    },

    isVisible() {
      return this.value;
    },

    logoUrl() {
      if (this.isSaas) {
        return this.appConfig?.saasLogo;
      }

      return '/icon.webp';
    },

    mustAcceptTerms() {
      return this.termsAccepted === false;
    },

    profileImage() {
      return this.userInfo?.profileImageUrl ?? null;
    },

    userRole() {
      if (this.isSuperAdmin) {
        return 'Super Administrador';
      }
      if (this.isAdminOrSuperAdmin) {
        return 'Administrador';
      }
      return null;
    },

    userEmail() {
      return this.userInfo?.email;
    },

    username() {
      return this.userInfo?.name ? this.userInfo.name.split(' ').slice(0, 2).join(' ') : '';
    },
  },

  methods: {
    handleMenuClick(item) {
      this.$emit('menu-click', item);
    },
  },
};
</script>

<style lang="scss" scoped>
.custom-sidebar {
  background: #ffffff;
  border-right: 1px solid #e5e7eb;
  display: flex;
  flex-direction: column;
  position: relative;
}

.sidebar-header {
  padding-top: 16px;

  .logo-container {
    display: flex;
    align-items: center;
    justify-content: center;

    .logo {
      max-height: 80px;
      width: auto;
    }
  }
}

.sidebar-content {
  flex: 1;
  overflow-y: auto;
  padding: 0;
}

.user-profile {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 0px 16px;

  .profile-info {
    display: flex;
    align-items: center;
    gap: 12px;

    .profile-avatar {
      width: 48px;
      height: 48px;
      border-radius: 50%;
      overflow: hidden;
      flex-shrink: 0;

      .avatar {
        width: 100%;
        height: 100%;
        object-fit: cover;
      }

      .avatar-placeholder {
        width: 100%;
        height: 100%;
        background: #6366f1;
        color: white;
        display: flex;
        align-items: center;
        justify-content: center;
        font-weight: 600;
        font-size: 18px;
      }
    }

    .profile-details {
      flex: 1;
      min-width: 0;

      .username {
        font-size: 16px;
        font-weight: 600;
        color: #111827;
        margin-bottom: 2px;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      .user-role {
        font-size: 14px;
        color: #6b7280;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }
    }
  }
}

.menu-section {
  margin-bottom: 8px;

  .section-title {
    padding: 16px 20px 8px;
    font-size: 12px;
    font-weight: 600;
    color: #6b7280;
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }

  .menu-items {
    padding: 0 8px;
  }

  &.bottom-section {
    margin-top: auto;
    margin-bottom: 0;
    padding-top: 16px;
    border-top: 1px solid #e5e7eb;
  }
}
</style>
